import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useTRPC, type RouterOutputs } from "@/trpc";
import { formatPaymentMethod } from "./payment-method-format";
import { PaymentMethodSetupDialog } from "./payment-method-setup-dialog";

type BillingAccountSummary = RouterOutputs["organization"]["getBillingAccount"];
function formatAuthorizedBy(
  account: NonNullable<BillingAccountSummary["account"]>,
): string | null {
  if (!account.paymentMethodAuthorizedAt) {
    return null;
  }
  const by = account.paymentMethodAuthorizedBy;
  const name = [by?.firstName, by?.lastName].filter(Boolean).join(" ");
  const date = new Date(account.paymentMethodAuthorizedAt).toLocaleDateString();
  return name ? `Saved by ${name} on ${date}` : `Saved on ${date}`;
}

const BILLING_ACCOUNT_VERSION_PARAM = "billing_account_version";

function billingSetupReturnUrl({
  returnUrl,
  expectedVersion,
}: {
  returnUrl: string;
  expectedVersion: number;
}): string {
  const url = new URL(returnUrl);
  url.searchParams.set(BILLING_ACCOUNT_VERSION_PARAM, String(expectedVersion));
  return url.toString();
}

export function PaymentMethodCard({
  organizationBusinessUnitId,
  summary,
  canManage,
  returnUrl,
}: {
  organizationBusinessUnitId: string;
  summary: BillingAccountSummary;
  canManage: boolean;
  returnUrl: string;
}) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [isRemoveOpen, setIsRemoveOpen] = useState(false);
  const { account } = summary;
  const paymentMethod = account?.defaultPaymentMethod ?? null;

  async function refresh() {
    await queryClient.invalidateQueries(
      trpc.organization.getBillingAccount.queryFilter({
        organizationBusinessUnitId,
      }),
    );
  }

  const setupMutation = useMutation(
    trpc.organization.createPaymentMethodSetupIntent.mutationOptions({
      onSuccess: (result) => setClientSecret(result.clientSecret),
      onError: (error) => {
        toast.error("Couldn't start payment method setup", {
          description: error.message,
        });
      },
    }),
  );

  const confirmMutation = useMutation(
    trpc.organization.confirmDefaultPaymentMethod.mutationOptions({
      onSuccess: async () => {
        setClientSecret(null);
        toast.success("Payment method saved");
        await refresh();
      },
      onError: (error) => {
        toast.error("Couldn't save the payment method", {
          description: error.message,
        });
      },
    }),
  );

  const removeMutation = useMutation(
    trpc.organization.removePaymentMethod.mutationOptions({
      onSuccess: async () => {
        setIsRemoveOpen(false);
        toast.success("Payment method removed");
        await refresh();
      },
      onError: (error) => {
        toast.error("Couldn't remove the payment method", {
          description: error.message,
        });
      },
    }),
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle>Payment method</CardTitle>
        <CardDescription>
          A saved card or US bank account lets Saile collect invoices for{" "}
          {summary.unitName} without a manual payment.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {paymentMethod ? (
          <div className="space-y-1">
            <p className="font-medium">{formatPaymentMethod(paymentMethod)}</p>
            {account && (
              <p className="text-sm text-muted-foreground">
                {formatAuthorizedBy(account)}
              </p>
            )}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">
            No payment method on file.
          </p>
        )}
        {canManage && (
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              className="w-full sm:w-auto"
              disabled={!account || setupMutation.isPending}
              onClick={() =>
                setupMutation.mutate({ organizationBusinessUnitId })
              }
            >
              {paymentMethod ? "Replace payment method" : "Add payment method"}
            </Button>
            {paymentMethod && (
              <Button
                type="button"
                className="w-full sm:w-auto"
                variant="outline"
                onClick={() => setIsRemoveOpen(true)}
              >
                Remove payment method
              </Button>
            )}
            {!account && (
              <p className="w-full text-sm text-muted-foreground">
                Save the billing contact before adding a payment method.
              </p>
            )}
          </div>
        )}
      </CardContent>

      <PaymentMethodSetupDialog
        clientSecret={clientSecret}
        unitName={summary.unitName}
        returnUrl={
          account
            ? billingSetupReturnUrl({
                returnUrl,
                expectedVersion: account.version,
              })
            : returnUrl
        }
        onOpenChange={(open) => {
          if (!open) {
            setClientSecret(null);
          }
        }}
        onConfirmed={async (setupIntentId) => {
          if (!account) {
            return;
          }
          await confirmMutation.mutateAsync({
            organizationBusinessUnitId,
            setupIntentId,
            expectedVersion: account.version,
          });
        }}
      />

      <AlertDialog open={isRemoveOpen} onOpenChange={setIsRemoveOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Remove the saved payment method?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Invoices for {summary.unitName} will need to be paid manually
              until a new payment method is saved.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={removeMutation.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={!account || removeMutation.isPending}
              onClick={(event) => {
                event.preventDefault();
                if (account) {
                  removeMutation.mutate({
                    organizationBusinessUnitId,
                    expectedVersion: account.version,
                  });
                }
              }}
            >
              Remove payment method
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
